import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import type { Broker } from "@freightsimple/generated-dashboard-openapi-client/models/Broker";
import type { SavedBroker } from "@freightsimple/generated-dashboard-openapi-client/models/SavedBroker";
import type { UUID } from "@/Shared/types";
import { useState } from "react";
import { BrokersModifyDialogBody } from "../CustomComponents/Internal/brokersModifyDialogBody";
import BrokersModifyBrokerDialogAddressPage from "../Pages/BrokersModifyBrokerDialogAddressPage";
import BrokersModifyBrokerDialogContactPage from "../Pages/BrokersModifyBrokerDialogContactPage";

export interface BrokersModifyResult {
  broker: Broker;
  savedBrokerId: UUID;
}

interface BrokersModifyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brokerToModify: SavedBroker;
  onFinished: (result: BrokersModifyResult) => void;
}

// Two-page modify-broker wizard (address -> contact), hosted in a ui-core
// Dialog. The pages render their own Cancel/Next/Previous/Save buttons, so
// the dialog supplies no footer. Remount with a fresh `key` per open to
// reset the wizard state.
export function BrokersModifyDialog(props: BrokersModifyDialogProps) {
  const [broker, setBroker] = useState<Broker>(props.brokerToModify.broker);

  const [currentPage, setCurrentPage] = useState<"address" | "contact">(
    "address",
  );

  function onDone() {
    props.onFinished({
      broker,
      savedBrokerId: props.brokerToModify.savedBrokerId,
    });
  }

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title="Modify Broker"
      // Cargo sized this form to a 520px address field, and its City /
      // State / Postal row adds up to exactly that. `narrow` leaves ~448px
      // of content, which squeezed every field.
      width="default"
    >
      <ControlPresentationProvider presentation="underlined">
        {/* Both pages keep the same height so the dialog doesn't jump when
         * switching between them (address page is the taller one). */}
        <BrokersModifyDialogBody>
          {currentPage === "address" && (
            <BrokersModifyBrokerDialogAddressPage
              broker={broker}
              setBroker={setBroker}
              onCancel={() => props.onOpenChange(false)}
              onSwitchToContactPage={() => setCurrentPage("contact")}
            />
          )}

          {currentPage === "contact" && (
            <BrokersModifyBrokerDialogContactPage
              contact={broker.contact}
              contactChanged={function (updatedContact) {
                setBroker({
                  ...broker,
                  contact: {
                    ...broker.contact,
                    ...updatedContact,
                  },
                });
              }}
              onPrevious={() => setCurrentPage("address")}
              onDone={onDone}
            />
          )}
        </BrokersModifyDialogBody>
      </ControlPresentationProvider>
    </Dialog>
  );
}
